/**
 * Hook para gerenciar estado offline e sincronização
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  initDB,
  isOnline,
  syncDataFromServer,
  syncPendingItems,
  hasPendingSync,
  getLastSyncTime,
  getPendingSyncItems
} from '../utils/offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useOffline = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [dbReady, setDbReady] = useState(false);

  // Inicializar banco de dados
  useEffect(() => {
    initDB()
      .then(() => {
        setDbReady(true);
        checkPendingSync();
        getLastSyncTime().then(setLastSync);
      })
      .catch((err) => {
        console.error('Erro ao inicializar DB:', err);
      });
  }, []);

  // Monitorar status de conexão
  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      toast.success('📶 Conexão restaurada');
      
      // Auto-sync quando voltar online
      const token = localStorage.getItem('token');
      if (token) {
        syncAll(token);
      }
    };

    const handleOffline = () => {
      setOnline(false);
      toast.warning('📴 Você está offline. Os dados serão salvos localmente.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Escutar mensagens do Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      };
    }
  }, []);

  const handleSWMessage = useCallback((event) => {
    if (event.data?.type === 'SYNC_REQUIRED') {
      const token = localStorage.getItem('token');
      if (token) {
        syncPending(token);
      }
    }
  }, []);

  // Verificar itens pendentes
  const checkPendingSync = useCallback(async () => {
    try {
      const items = await getPendingSyncItems();
      setPendingCount(items.length);
    } catch (error) {
      console.error('Erro ao verificar pendentes:', error);
    }
  }, []);

  // Sincronizar dados do servidor para cache local
  const cacheServerData = useCallback(async (token) => {
    if (!online) {
      console.log('Offline - não é possível cachear dados');
      return;
    }

    setSyncing(true);
    try {
      const result = await syncDataFromServer(API_URL, token);
      if (result.success) {
        setLastSync(new Date().toISOString());
        toast.success('✅ Dados sincronizados para uso offline');
      }
    } catch (error) {
      console.error('Erro ao cachear dados:', error);
    } finally {
      setSyncing(false);
    }
  }, [online]);

  // Sincronizar itens pendentes com o servidor
  const syncPending = useCallback(async (token) => {
    if (!online) {
      console.log('Offline - não é possível sincronizar');
      return;
    }

    const hasPending = await hasPendingSync();
    if (!hasPending) return;

    setSyncing(true);
    try {
      const result = await syncPendingItems(API_URL, token);
      
      if (result.synced > 0) {
        toast.success(`✅ ${result.synced} item(s) sincronizado(s)`);
      }

      if (result.deferred > 0) {
        if (result.cloudinaryLimitHit) {
          toast.error('Limite da conta Cloudinary atingido. Avise o gestor para verificar a conta. As fotos seguem salvas localmente — reenvie manualmente em cada foto.');
        } else {
          toast.warning(`⏳ ${result.deferred} O.S. com foto(s) pendente(s). As fotos seguem salvas localmente; use "Reenviar" em cada foto.`);
        }
      }

      if (result.failed > 0) {
        toast.error(`❌ ${result.failed} item(s) falharam na sincronização`);
      }

      await checkPendingSync();
    } catch (error) {
      console.error('Erro ao sincronizar pendentes:', error);
      toast.error('Erro ao sincronizar dados');
    } finally {
      setSyncing(false);
    }
  }, [online, checkPendingSync]);

  // Sincronização completa (baixar + upload)
  const syncAll = useCallback(async (token) => {
    if (!online) return;

    setSyncing(true);
    try {
      // Primeiro, enviar pendentes
      await syncPending(token);
      
      // Depois, atualizar cache local
      await cacheServerData(token);
    } finally {
      setSyncing(false);
    }
  }, [online, syncPending, cacheServerData]);

  // Forçar atualização do cache
  const forceRefresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token && online) {
      await syncAll(token);
    }
  }, [online, syncAll]);

  return {
    online,
    syncing,
    pendingCount,
    lastSync,
    dbReady,
    syncAll,
    syncPending,
    cacheServerData,
    forceRefresh,
    checkPendingSync
  };
};

export default useOffline;
